import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { onValue, ref } from 'firebase/database';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import './projects.css';
import './ProjectDetail.css';
import Gallery from './Gallery.js';
import YouTubePlayer from './YouTubePlayer';

const slugify = (str = '') => str.toString().toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')
  .trim()
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-');

const dispatchZoomOut = () => {
  const x = window.innerWidth / 2;
  const y = window.innerHeight / 2;
  document.documentElement.style.setProperty('--zoom-origin', `${x}px ${y}px`);
  window.dispatchEvent(new CustomEvent('blossom-zoom', {
    detail: { phase: 'out', x, y }
  }));
};

const friendlyKey = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();

const renderDescriptionValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((para, i) => (
      <p key={i} className="pd-prose">{String(para)}</p>
    ));
  }
  if (value && typeof value === 'object') {
    return (
      <ul className="pd-list">
        {Object.entries(value).map(([k, v]) => (
          <li key={k}><strong>{friendlyKey(k)}:</strong> {String(v)}</li>
        ))}
      </ul>
    );
  }
  // Long strings often contain hard newlines for paragraph breaks.
  const text = String(value);
  const paragraphs = text.split(/\n\s*\n|\n-\s/g).map(s => s.trim()).filter(Boolean);
  return paragraphs.map((para, i) => (
    <p key={i} className="pd-prose">{para}</p>
  ));
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const query = ref(db, 'Projects');
    const unsub = onValue(query, snap => {
      try {
        const data = snap.val();
        if (!data) { setLoading(false); return; }
        const list = Object.values(data).sort((a, b) => new Date(b.date) - new Date(a.date));
        setAllProjects(list);
        const found = list.find(p => slugify(p.projectName) === slug);
        if (!found) {
          setError('Project not found');
        } else {
          setProject(found);
        }
        setLoading(false);
      } catch (e) {
        console.error(e);
        setError('Failed to load project');
        setLoading(false);
      }
    }, err => { setError(err.message); setLoading(false); });
    return () => unsub();
  }, [slug]);

  const related = useMemo(() => {
    if (!project || !allProjects.length) return [];
    const others = allProjects.filter(p => p !== project);
    return [...others].sort(() => 0.5 - Math.random()).slice(0, 3);
  }, [project, allProjects]);

  if (loading) return <div className="pd-loading">Loading project…</div>;
  if (error) return (
    <div className="project-detail-skills-style">
      <div className="project-error">
        {error}
        <button onClick={() => navigate('/Projects')}>Back</button>
      </div>
    </div>
  );
  if (!project) return null;

  const year = project.date ? new Date(project.date).getFullYear() : null;
  const company = project.Company;
  const locations = project.Locations;
  const videos = Array.isArray(project.Videos)
    ? project.Videos.filter(Boolean)
    : (typeof project.Videos === 'string' && project.Videos ? [project.Videos] : []);

  const descriptionEntries = (() => {
    const d = project.description;
    if (!d) return [];
    if (typeof d === 'string') return [['Description', d]];
    if (Array.isArray(d)) return [['Description', d]];
    if (typeof d === 'object') return Object.entries(d);
    return [];
  })();

  return (
    <main className="pd-page" aria-label="Project Detail">
      <button
        type="button"
        className="pd-back"
        onClick={() => { dispatchZoomOut(); navigate('/Projects'); }}
      >
        <span aria-hidden="true">&larr;</span> All Projects
      </button>

      <header className="pd-hero">
        <p className="pd-eyebrow">
          {[year, company, locations].filter(Boolean).join(' • ')}
        </p>
        <h1 className="pd-title">{project.projectName}</h1>
        {project.ShortD && <p className="pd-lede">{project.ShortD}</p>}
      </header>

      {videos.length > 0 ? (
        <section className="pd-video" aria-label="Project video">
          <YouTubePlayer videoIds={videos} />
        </section>
      ) : project.image ? (
        <figure className="pd-cover">
          <img src={project.image} alt={project.projectName} loading="eager" decoding="async" />
        </figure>
      ) : null}

      <Gallery
        imagesPath={project?.images}
        projectName={project.projectName}
      />

      {descriptionEntries.length > 0 && (
        <section className="pd-body">
          {descriptionEntries.map(([key, value]) => (
            <div key={key} className="pd-section">
              <h2 className="pd-section-heading">{friendlyKey(key)}</h2>
              <div className="pd-section-body">
                {renderDescriptionValue(value)}
              </div>
            </div>
          ))}
        </section>
      )}

      {project.links && Array.isArray(project.links) && project.links.length > 0 && (
        <section className="pd-links">
          <h2 className="pd-section-heading">Press &amp; Links</h2>
          <ul className="pd-links-list">
            {project.links.map((link, idx) => (
              <li key={idx}>
                <a href={link} target="_blank" rel="noopener noreferrer" className="pd-link">
                  {link.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {related.length > 0 && (
        <section className="pd-related">
          <div className="pd-divider" aria-hidden="true" />
          <h2 className="pd-section-heading">More projects</h2>
          <div className="pd-related-list">
            {related.map((p, idx) => {
              const otherSlug = slugify(p.projectName || 'project');
              return (
                <button
                  type="button"
                  key={idx}
                  className="pd-related-card"
                  aria-label={`View project ${p.projectName}`}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = rect.left + rect.width / 2;
                    const y = rect.top + rect.height / 2;
                    document.documentElement.style.setProperty('--zoom-origin', `${x}px ${y}px`);
                    window.dispatchEvent(new CustomEvent('blossom-zoom', {
                      detail: { phase: 'in', x, y }
                    }));
                    navigate(`/projects/${otherSlug}`);
                  }}
                >
                  <div className="pd-related-img">
                    {p.image && <LazyLoadImage src={p.image} alt={p.projectName} placeholderSrc={null} />}
                  </div>
                  <div className="pd-related-meta">
                    <div className="pd-related-title">{p.projectName}</div>
                    {p.date && <div className="pd-related-date">{new Date(p.date).getFullYear()}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProjectDetail;
